import { Component, ElementRef, HostListener, OnInit, ViewChild, signal } from '@angular/core';
import { UserComponent } from './dialogs/user/user.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CreditsHistoryComponent } from './dialogs/credits-history/credits-history.component';
import { ChangeHiddenStatusComponent } from './dialogs/change-hidden-status/change-hidden-status.component';
import { UserFutureRidesComponent } from './dialogs/user-future-rides/user-future-rides.component';
import {
  addDoc,
  collection,
  doc,
  deleteField,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { ChangeCreditsComponent } from './dialogs/change-credits/change-credits.component';
import { environment } from 'src/environments/environment';
import {
  FailedImportRow,
  GenericWarningDialogData,
  OrgUser,
  Particularity,
  PublicSettings,
  Setting,
  User,
  UserId,
  UserWithWarnings,
} from '../interfaces';
import {
  isExcelFile,
  logExport,
  redirectIfDriver,
  translateAddressError,
  ucFirst,
  valueToDate,
  exportJsonToExcel,
  readExcelFile,
} from '../globals';
import { Router } from '@angular/router';
import { getAuth } from 'firebase/auth';
import { CreateSepaComponent } from './dialogs/create-sepa/create-sepa.component';
import { PaymentMethod, FilterOption, Role } from 'src/app/enums';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { WarningDialog } from '../global-dialogs/warning/warning.component';
import { dataStore, db, functions } from '../app.component';

import {
  ImportExportComponent,
  UserExportSelection,
} from '../settings/dialogs/import-export/import-export.component';
import { httpsCallable } from 'firebase/functions';
import { AddressValidationService } from '../services/address-validation.service';
import { firstValueFrom } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton, MatFabButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll } from '@angular/cdk/scrolling';
import { NgClass } from '@angular/common';
import { MatChip } from '@angular/material/chips';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatProgressBar } from '@angular/material/progress-bar';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  imports: [
    MatIcon,
    MatIconButton,
    MatCard,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSuffix,
    MatButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatTooltip,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    NgClass,
    MatChip,
    MatProgressSpinner,
    MatProgressBar,
    MatFabButton,
  ],
})
@HostListener('window:scroll', ['$event'])
export class UsersComponent implements OnInit {
  @ViewChild('uploader', { static: false }) uploader: ElementRef;
  itemsToShow = 20;
  askBirthDay: boolean;
  env = environment;
  currentUser: User;
  publicSettings: PublicSettings;
  filterOption = FilterOption;

  filteredUsers = signal<any[]>([]);
  displayedColumns = [];
  filterForm: FormGroup = new FormGroup({
    search: new FormControl(''),
    aToZ: new FormControl(true),
    cardFilter: new FormControl<FilterOption | null>(null),
    planFilter: new FormControl<FilterOption | null>(null),
    paymentMethodFilter: new FormControl<FilterOption | null>(null),
    warningFilter: new FormControl<FilterOption | null>(null),
    showHidden: new FormControl(false),
  });
  generalSettings: any;

  allUsersList: UserWithWarnings[] = [];
  filteredUsersList: UserWithWarnings[] = [];
  particularities: Particularity[] = dataStore.selectedOrganisation.particularities;
  // Used for the delete button
  fileUploaded: any;
  organisationId = dataStore.selectedOrganisation.id;
  shouldShowSepaButton = signal(false);
  roles: Role[];

  moment = moment;
  valueToDate = valueToDate;
  loading = signal(true);
  processing = signal(false);
  importing = signal(false);
  importProgress = signal(0);
  isSuperUser = signal(false);
  isOnlyRidePlanner = signal(false);

  constructor(
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router,
    public addressValidationService: AddressValidationService,
  ) {}

  canDeactivate(): Promise<boolean> | boolean {
    if (this.processing()) {
      const dialogRef = this.dialog.open(WarningDialog, {
        width: '500px',
        data: {
          dialogType: 'confirmDeactivateImport',
        },
      });
      return firstValueFrom(dialogRef.afterClosed()).then((res) => !!res);
    }
    return true;
  }

  async ngOnInit(): Promise<void> {
    const currentUserId = getAuth().currentUser?.uid;
    this.currentUser = {
      ...((await getDoc(doc(db, `users/${currentUserId}`))).data() as User),
      id: currentUserId,
    };

    const organisationObj = this.currentUser.organisations.find((obj) => {
      return obj.id === this.organisationId;
    });

    if (
      this.currentUser.rights === 'admin' ||
      organisationObj.roles?.includes(Role.Administrator)
    ) {
      this.shouldShowSepaButton.set(true);
    }
    if (this.currentUser.rights === 'admin') {
      this.isSuperUser.set(true);
    }
    this.roles = organisationObj.roles;

    this.publicSettings = (
      await getDoc(doc(db, `organisations/${this.organisationId}/settings/public`))
    ).data() as PublicSettings;

    this.generalSettings = (
      await getDoc(doc(db, `organisations/${this.organisationId}/settings/general`))
    ).data() as Setting;

    const isOnlyRidePlanner =
      !this.isSuperUser() &&
      this.roles.includes(Role.RidePlanner) &&
      !this.roles.includes(Role.Planner) &&
      !this.roles.includes(Role.Administrator);
    this.isOnlyRidePlanner.set(isOnlyRidePlanner);

    this.askBirthDay =
      (this.publicSettings ? this.publicSettings.askBirthDay : false) && !isOnlyRidePlanner;

    const usersQuery = query(
      collection(db, `organisations/${this.organisationId}/users`),
      where(`passenger`, `==`, true),
    );
    onSnapshot(usersQuery, (userSnapshot) => {
      const usersList: UserWithWarnings[] = [];
      userSnapshot.forEach((user) => {
        const userData = user.data() as UserWithWarnings;
        if (this.isSuperUser() || this.checkAllowed(userData)) {
          if (!userData.credits) {
            userData.credits = 0;
          }
          const id = user.id;
          if (userData.sepaData && userData.sepaData.hasOpenSepa && userData.sepaData.sepaSentOn) {
            userData.sepaData.sepaSentOn = userData.sepaData.sepaSentOn.toDate();
          }
          userData.warnings = this.getWarnings(userData);
          userData.name = ucFirst(userData?.name ?? '');
          usersList.push({ id: id, ...userData } as UserWithWarnings);
        }
      });
      this.allUsersList = usersList;
      this.filterUsers();
    });

    this.filterForm.valueChanges.subscribe(() => {
      this.filterUsers();
    });
    redirectIfDriver(this.router);
    this.loading.set(false);
  }

  clearSearch() {
    this.filterForm.controls.search.setValue('');
  }

  copyEmail(email) {
    const el = document.createElement('textarea');
    el.value = email;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    this.snackBar.open('E-mail is gekopieerd.', 'X', {
      duration: 3000,
    });
  }

  filterUsers() {
    this.filteredUsers.set(
      this.allUsersList.filter((user) => {
        if (user.deleted) {
          return false;
        }

        let passesHiddenFilter: boolean;
        let passesSearchFilter = true;
        const filters = this.filterForm.value;

        if ((filters.showHidden && user.hidden) || (!filters.showHidden && !user.hidden)) {
          passesHiddenFilter = true;
        } else {
          passesHiddenFilter = false;
        }
        if (filters.search) {
          const searchQuery = filters.search.toLowerCase();
          passesSearchFilter = false;
          if (user.id) {
            if (user.id.toLowerCase() === searchQuery) {
              passesSearchFilter = true;
            }
          }
          if (user.name) {
            if (user.name.toLowerCase().includes(searchQuery)) {
              passesSearchFilter = true;
            }
          }
          if (user.email) {
            if (user.email.toLowerCase().includes(searchQuery)) {
              passesSearchFilter = true;
            }
          }
          if (user.phoneNumber) {
            if (user.phoneNumber.toLowerCase().includes(searchQuery)) {
              passesSearchFilter = true;
            }
          }
          if (user.cardId) {
            if (user.cardId.toLowerCase().includes(searchQuery)) {
              passesSearchFilter = true;
            }
          }
          if (user.postal) {
            if (user.postal.toLowerCase().includes(searchQuery)) {
              passesSearchFilter = true;
            }
          }
          if (user.streetName) {
            if (user.streetName.toLowerCase().includes(searchQuery)) {
              passesSearchFilter = true;
            }
          }
          if (user.city) {
            if (user.city.toLowerCase().includes(searchQuery)) {
              passesSearchFilter = true;
            }
          }
          if (user.hiddenRemarks && typeof user.hiddenRemarks === 'string') {
            if (user.hiddenRemarks.toLowerCase().includes(searchQuery)) {
              passesSearchFilter = true;
            }
          }
          if (user.currentPlan) {
            if (
              user.currentPlan.name &&
              user.currentPlan.name.toLowerCase().includes(searchQuery)
            ) {
              passesSearchFilter = true;
            }
            if (
              user.currentPlan.description &&
              user.currentPlan.description.toLowerCase().includes(searchQuery)
            ) {
              passesSearchFilter = true;
            }
          }
        }
        const selectedFilters = [
          filters.cardFilter,
          filters.planFilter,
          filters.paymentMethodFilter,
          filters.warningFilter,
        ].filter(Boolean) as FilterOption[];
        const passesFilter = selectedFilters.every((filter) => this.userPassesFilter(user, filter));

        if (this.currentUser.rights !== 'admin') {
          if (user.superuser) {
            passesHiddenFilter = false;
            passesSearchFilter = false;
          }
        }

        if (passesSearchFilter && passesHiddenFilter && passesFilter) {
          return true;
        }
        return false;
      }),
    );
    this.sortUsers();
  }

  checkAllowed(user: UserWithWarnings) {
    const isAdmin = this.roles?.includes(Role.Administrator);
    if (!user.superuser && (!user.roles || user.roles.length === 0 || isAdmin)) {
      return true;
    }
    return false;
  }

  sortUsers() {
    const aToZ: boolean = this.filterForm.value.aToZ ?? true;
    this.filteredUsers.update((users) => {
      return [...users].sort((a, b) => {
        const nameA = a.name ? a.name.toLowerCase() : '';
        const nameB = b.name ? b.name.toLowerCase() : '';
        if (nameA < nameB) {
          return aToZ ? -1 : 1;
        }
        if (nameA > nameB) {
          return aToZ ? 1 : -1;
        }
        return 0;
      });
    });
  }

  async changeCredits(type, user) {
    const dialogRef = this.dialog.open(ChangeCreditsComponent, {
      width: '370px',
      data: { type, userId: user.id },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (!result) {
        return;
      }

      const pinAdd = Number(this.generalSettings.pinAddCredits);
      const pinSubtract = Number(this.generalSettings.pinSubtractCredits);

      if (result.credits) {
        if (this.generalSettings.askPin) {
          if (!result.pin) {
            this.snackBar.open('Er is geen pincode ingevoerd.', '', {
              duration: 5000,
            });
            return;
          }
          if (!pinAdd || !pinSubtract) {
            return this.showError('Er is nog geen pincode ingesteld bij de instellingen.');
          }
          const pin = result.pin;
          if (type === 'add' && pinAdd !== pin) {
            return this.showError('De pincode is onjuist.');
          }
          if (type === 'remove' && pinSubtract !== pin) {
            return this.showError('De pincode is onjuist.');
          }
        }
        let oldCredits: number;
        let newCredits: number;

        const userDocRef = doc(db, `organisations/${this.organisationId}/users/${user.id}`);

        await runTransaction(db, async (transaction) => {
          const userDoc = await transaction.get(userDocRef);
          if (userDoc.exists) {
            oldCredits = userDoc.data().credits || 0;
            if (type === 'add') {
              newCredits = oldCredits + result.credits;
            }
            if (type === 'remove') {
              newCredits = oldCredits - result.credits;
            }
            transaction.update(userDocRef, {
              credits: newCredits,
              paymentFailed: false,
            });
          }
        });
        let saveData;
        if (type === 'add') {
          saveData = {
            userId: user.id,
            userRef: doc(db, `users/${user.id}`),
            date: new Date(),
            creditsAdded: result.credits,
            // description: 'Handmatige bijschrijving',
            description: 'Contante betaling',
            status: 'paid',
            type: 'cash',
            payedToWemaron: false,
            actionBy: this.currentUser.email,
          };
          if (result.remarks) {
            saveData.remarks = result.remarks;
          }

          await addDoc(collection(db, `organisations/${this.organisationId}/charges`), saveData);
        }
        if (type === 'remove') {
          saveData = {
            userId: user.id,
            userRef: doc(db, `users/${user.id}`),
            date: new Date(),
            creditsSpent: result.credits,
            description: 'Handmatige afschrijving',
            actionBy: this.currentUser.email,
          };
          if (result.remarks) {
            saveData.remarks = result.remarks;
          }

          await addDoc(collection(db, `organisations/${this.organisationId}/charges`), saveData);
        }
        if (type === 'add') {
          this.snackBar.open('De credits zijn toegevoegd.', 'X', {
            duration: 5000,
          });
        }
        if (type === 'remove') {
          this.snackBar.open('De credits zijn verminderd.', 'X', {
            duration: 5000,
          });
        }
      }
    });
  }

  async showCreditsHistory(user) {
    this.dialog.open(CreditsHistoryComponent, {
      data: { user },
    });
  }

  async showFutureRides(user) {
    this.dialog.open(UserFutureRidesComponent, {
      width: '600px',
      data: { user },
    });
  }

  async changeHiddenStatus(status, user) {
    const dialogRef = this.dialog.open(ChangeHiddenStatusComponent, {
      width: '425px',
      data: { type: status, user },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        try {
          const hiddenUpdate =
            status === 'hide'
              ? { hidden: true, hiddenDate: serverTimestamp() }
              : { hidden: false, hiddenDate: deleteField() };
          await setDoc(
            doc(db, `organisations/${this.organisationId}/users/${user.id}`),
            hiddenUpdate,
            { merge: true },
          );
          this.snackBar.open(
            status === 'hide' ? 'De passagier is verborgen.' : 'De passagier is weer zichtbaar.',
            '',
            { duration: 5000 },
          );
        } catch (error) {
          console.error('Fout bij het aanpassen van de zichtbaarheid:', error);
          this.snackBar.open('De zichtbaarheid van de passagier kon niet worden aangepast.', '', {
            duration: 5000,
          });
        }
      }
    });
  }

  async editUser(user) {
    const publicSettings = this.publicSettings;
    const generalSettings = this.generalSettings;
    const roles = this.roles;
    this.dialog.open(UserComponent, {
      panelClass: 'fullscreen-dialog',
      autoFocus: false,
      data: { publicSettings, generalSettings, roles, user },
    });
  }

  async deleteUser(user) {
    if (user.id == this.currentUser.id) {
      this.snackBar.open('Je kunt jezelf niet verwijderen.', '', {
        duration: 5000,
      });
      return;
    }
    if (user.currentPlan) {
      this.snackBar.open(
        'Deze passagier heeft nog een gekoppeld betaalplan en kan niet worden verwijderd.',
        '',
        {
          duration: 5000,
        },
      );
      return;
    }
    const paymentsRef = query(
      collection(getFirestore(), `organisations/${this.organisationId}/payments`),
      where('userId', '==', user.id),
      orderBy('date', 'desc'),
      limit(1),
    );
    const payments = await getDocs(paymentsRef);
    let lastPayment = null;
    if (payments.size > 0) {
      lastPayment = payments.docs[0].data();
    }
    const dialogRef = this.dialog.open(WarningDialog, {
      width: '500px',
      autoFocus: false,
      data: {
        dialogType: 'deleteUser',
        optionalData: {
          userName: user.name,
          userHasCard: user.cardId ? true : false,
          deleteMode: true,
        },
      } as GenericWarningDialogData,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        try {
          if (user.roles?.length > 0 || user.superuser) {
            //userdoc should not be deleted for users with an Role or superUsers. Set passenger status to false instead.
            await updateDoc(doc(db, `organisations/${this.organisationId}/users/${user.id}`), {
              passenger: false,
            });
          } else {
            await setDoc(
              doc(db, `organisations/${this.organisationId}/deleteUsersQueue/${user.id}`),
              {
                email: user.email ?? '',
                phone: user.phone ?? '',
                date: new Date(),
                status: 'todo',
                userData: user,
                adminEmail: this.currentUser.email,
              },
              { merge: true },
            );
            this.allUsersList = this.allUsersList.map((listedUser) =>
              listedUser.id === user.id
                ? { ...listedUser, deleted: true, hidden: true, inactive: true }
                : listedUser,
            );
            this.filterUsers();

            await addDoc(collection(db, `eventLogs/${this.organisationId}/deletedUser`), {
              date: new Date(),
              doneByUser: getAuth().currentUser.email,
              doneByUid: getAuth().currentUser.uid,
              email: user.email ?? '',
              phone: user.phone ?? '',
              userData: user,
            });
            if (lastPayment) {
              const lastPaymentMoment = moment(lastPayment.date.toDate()).add(2, 'years');
              this.snackBar.open(
                `De passagier wordt verwijderd op ${lastPaymentMoment.format('DD-MM-YYYY')}.`,
                '',
                {
                  duration: 5000,
                },
              );
            } else {
              this.snackBar.open('De passagier wordt binnenkort verwijderd.', '', {
                duration: 5000,
              });
            }
          }
        } catch (error) {
          console.log(error);
        }
      }
    });
  }
  newUser() {
    const publicSettings = this.publicSettings;
    const generalSettings = this.generalSettings;
    this.dialog.open(UserComponent, {
      panelClass: 'fullscreen-dialog',
      autoFocus: false,
      data: { publicSettings, generalSettings },
    });
  }

  showError(txt: string): void {
    this.snackBar.open(txt, 'X', {
      duration: 5000,
    });
  }

  requestSepaCreation() {
    this.dialog.open(CreateSepaComponent, {
      width: '400px',
      data: { users: this.allUsersList, plannerEmail: this.currentUser.email },
    });
  }

  setFilter(option: FilterOption) {
    const controlName = this.getFilterControlName(option);
    const control = this.filterForm.controls[controlName];
    control.setValue(control.value === option ? null : option);
  }

  clearAllFilters() {
    this.filterForm.patchValue({
      cardFilter: null,
      planFilter: null,
      paymentMethodFilter: null,
      warningFilter: null,
    });
  }

  hasActiveFilters(): boolean {
    return Boolean(
      this.filterForm.value.cardFilter ||
      this.filterForm.value.planFilter ||
      this.filterForm.value.paymentMethodFilter ||
      this.filterForm.value.warningFilter,
    );
  }

  private getFilterControlName(
    option: FilterOption,
  ): 'cardFilter' | 'planFilter' | 'paymentMethodFilter' | 'warningFilter' {
    if ([FilterOption.NoCard, FilterOption.HasCard].includes(option)) {
      return 'cardFilter';
    }
    if ([FilterOption.NoPlan, FilterOption.HasPlan].includes(option)) {
      return 'planFilter';
    }
    if ([FilterOption.PaymentMethodMollie, FilterOption.PaymentMethodSepa].includes(option)) {
      return 'paymentMethodFilter';
    }
    return 'warningFilter';
  }

  private userPassesFilter(user: UserWithWarnings, filter: FilterOption): boolean {
    switch (filter) {
      case FilterOption.NoCard:
        return !user.cardId;
      case FilterOption.HasCard:
        return Boolean(user.cardId);
      case FilterOption.NoPlan:
        return !user.currentPlan;
      case FilterOption.HasPlan:
        return Boolean(user.currentPlan);
      case FilterOption.PaymentMethodMollie:
        return user.paymentMethod === PaymentMethod.MOLLIE;
      case FilterOption.PaymentMethodSepa:
        return user.paymentMethod === PaymentMethod.SEPA;
      case FilterOption.WarningLowCredits:
      case FilterOption.WarningPaymentError:
      case FilterOption.WarningSendSepaMandate:
      case FilterOption.WarningUploadSepaMandate:
        return Boolean(user.warnings?.some((warning) => warning.id === filter));
      default:
        return true;
    }
  }

  getWarnings(user: UserWithWarnings) {
    const warnings = [];
    const userCreditWarning = user.currentPlan?.automaticLimit
      ? user.currentPlan?.automaticLimit
      : 5;

    if (user.paymentMethod === PaymentMethod.SEPA && !user.sepaMandateId) {
      warnings.push({
        id: 'sendSepaMandate',
        iconOrText: 'S',
        description: 'Er moet een SEPA machtiging verstuurd worden.',
        type: 'text',
        colorClass: 'orange-bg',
      });
    }
    if (user.credits < userCreditWarning) {
      warnings.push({
        id: 'lowCredits',
        iconOrText: 'priority_high',
        description: `De passagier heeft minder dan ${
          user.currentPlan?.automaticLimit ? user.currentPlan?.automaticLimit : 5
        } credits.`,
        type: 'icon',
        colorClass: 'orange-bg',
      });
    }
    if (user.paymentFailed) {
      warnings.push({
        id: 'paymentError',
        iconOrText: '€',
        description: 'Er is een fout opgetreden bij het betalen.',
        type: 'text',
        colorClass: 'red-bg',
      });
    }
    if (user.paymentMethod === PaymentMethod.SEPA && user.sepaMandateId && !user.sepaPath) {
      warnings.push({
        id: 'uploadSepaMandate',
        iconOrText: 'U',
        description: 'Upload SEPA machtiging.',
        type: 'text',
        colorClass: 'purple-bg',
      });
    }
    return warnings;
  }

  openImportExport() {
    const dialogRef = this.dialog.open(ImportExportComponent, {
      width: '450px',
      data: {
        type: 'users',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.type === 'Export' && result.usersToExport) {
        this.export(result.usersToExport);
      }
      if (result.type === 'Import') {
        this.uploader.nativeElement.click();
      }
    });
  }

  uploadedFile(event) {
    if (this.uploader.nativeElement.value === null) {
      return;
    } else {
      if (isExcelFile(event.target.files[0])) {
        const readFile = new FileReader();
        readFile.onload = async () => {
          this.fileUploaded = await readExcelFile(event.target.files[0]);
          this.import();
          this.uploader.nativeElement.value = '';
        };
        readFile.readAsArrayBuffer(event.target.files[0]);
      } else {
        this.snackBar.open('Het geselecteerde bestand moet een Excel-bestand zijn.', 'X', {
          duration: 3000,
        });
        this.uploader.nativeElement.value = '';
      }
    }
  }

  async import() {
    if (this.processing()) {
      return;
    }

    if (!this.fileUploaded || Object.keys(this.fileUploaded).length === 0) {
      this.showError('Bestand is leeg of kon niet worden gelezen.');
      return;
    }

    this.processing.set(true);
    this.importing.set(true);
    this.importProgress.set(0);

    try {
      const promises: Promise<any>[] = [];
      // The spreadsheet is already parsed by readExcelFile into a nested object: { rowId: { colId: value } }
      const spreadsheet = this.fileUploaded;

      const columnNames = spreadsheet[1];
      if (!columnNames) {
        this.showError(
          'Geen kolomnamen gevonden op de eerste rij. Controleer of het bestand correct is.',
        );
        return;
      }
      const originalColumnNames = { ...columnNames };
      const buildOriginalRow = (row: Record<string, any>): Record<string, any> => {
        const originalRow: Record<string, any> = {};
        Object.keys(row).forEach((columnKey) => {
          const header = originalColumnNames[columnKey];
          if (
            header &&
            header.toString().trim().toLowerCase() !== 'reden van fout bij importeren'
          ) {
            originalRow[header] = row[columnKey];
          }
        });
        return originalRow;
      };
      const emergencyPhoneNumberColumns = {};

      Object.keys(columnNames).forEach((key) => {
        const cellValue = columnNames[key];
        if (!cellValue) {
          delete columnNames[key];
          return;
        }
        const val = cellValue.toString().toLowerCase().trim();

        // Check for emergency phone number columns
        const emergencyPhoneMatch = val.match(/^noodnummer\s*(\d*)\s*(naam|telefoon|relatie)$/);
        if (emergencyPhoneMatch) {
          const index = emergencyPhoneMatch[1] ? parseInt(emergencyPhoneMatch[1]) : 0;
          const field = emergencyPhoneMatch[2];
          if (!emergencyPhoneNumberColumns[index]) {
            emergencyPhoneNumberColumns[index] = {};
          }
          emergencyPhoneNumberColumns[index][field] = key;
          columnNames[key] = `emergencyPhone_${index}_${field}`;
          return;
        }

        /* eslint-disable no-case-declarations */
        switch (val) {
          default:
            const foundParticularity = this.particularities?.find(
              ({ name }) => name?.toLowerCase() === val,
            );
            if (foundParticularity) {
              columnNames[key] = foundParticularity.id;
            } else {
              delete columnNames[key];
            }
            break;
          case 'passagier id':
            columnNames[key] = 'userId';
            break;
          case 'rol':
          case 'rollen':
            columnNames[key] = 'roles';
            break;
          case 'e-mail':
            columnNames[key] = 'email';
            break;
          case 'geboortedatum':
            columnNames[key] = 'birthDate';
            break;
          case 'kaartnummer':
            columnNames[key] = 'cardId';
            break;
          case 'naam':
            columnNames[key] = 'name';
            break;
          case 'stad':
            columnNames[key] = 'city';
            break;
          case 'straatnaam':
            columnNames[key] = 'streetName';
            break;
          case 'huisnummer':
            columnNames[key] = 'houseNumber';
            break;
          case 'huisnummer toevoeging':
            columnNames[key] = 'houseNumberAddition';
            break;
          case 'postcode':
            columnNames[key] = 'postal';
            break;
          case 'telefoonnummer':
            columnNames[key] = 'phoneNumber';
            break;
          case 'acceptatiedatum privacybeleid':
            columnNames[key] = 'privacyPolicyAcceptDate';
            break;
          case 'geslacht':
            columnNames[key] = 'gender';
            break;
          case 'opmerking voor planner':
            columnNames[key] = 'remarks';
            break;
          case 'opmerking voor chauffeur':
            columnNames[key] = 'commentsForDriver';
            break;
          case 'opmerking (algemeen)':
            columnNames[key] = 'hiddenRemarks';
            break;
          case 'verborgen':
            columnNames[key] = 'hidden';
            break;
        }
        /* eslint-enable no-case-declarations */
      });

      delete spreadsheet[1];
      // console.log('Final column names mapping:', columnNames);
      // console.log('spreadsheet data:', spreadsheet);

      // First pass: collect all user data
      let usersToImport: {
        userId?: string;
        userData: UserId;
        fullAddress?: string;
        originalRow: Record<string, any>;
      }[] = [];

      Object.keys(spreadsheet).forEach((key) => {
        let userId: string;
        const userData = {} as UserId;
        const emergencyPhoneNumbers = {};

        Object.keys(spreadsheet[key]).forEach((colKey) => {
          const colName = columnNames[colKey];
          const rawValue = spreadsheet[key][colKey];
          if (
            colName &&
            rawValue !== undefined &&
            rawValue !== null &&
            rawValue.toString().length !== 0
          ) {
            const value = rawValue;
            if (value && !value.toString().includes('N.V.T')) {
              // Check if it's an emergency phone number field
              const emergencyMatch = colName.match(
                /^emergencyPhone_(\d+)_(naam|telefoon|relatie)$/,
              );
              if (emergencyMatch) {
                const index = parseInt(emergencyMatch[1]);
                const field = emergencyMatch[2];
                if (!emergencyPhoneNumbers[index]) {
                  emergencyPhoneNumbers[index] = {};
                }
                const fieldMap = {
                  naam: 'name',
                  telefoon: 'phoneNumber',
                  relatie: 'description',
                };
                emergencyPhoneNumbers[index][fieldMap[field]] = value;
                return;
              }

              // Check if this is a particularity column
              const foundParticularity = this.particularities?.find(({ id }) => id === colName);
              if (foundParticularity) {
                if (!userData.particularityIds) {
                  userData.particularityIds = [];
                }
                // Only add to array if value is explicitly "WAAR" (TRUE)
                const valueStr = value?.toString().toUpperCase();
                if (valueStr === 'WAAR') {
                  userData.particularityIds.push(foundParticularity.id);
                }
                return;
              }

              /* eslint-disable no-case-declarations */
              switch (colName) {
                default:
                  userData[colName] = spreadsheet[key][colKey];
                  const foundParticularity = this.particularities?.find(({ id }) => id === colName);
                  if (foundParticularity) {
                    if (!userData.particularityIds) {
                      userData.particularityIds = [];
                    }
                    userData.particularityIds.push(foundParticularity.id);
                    delete userData[colName];
                  }
                  break;
                case 'userId':
                  userId = spreadsheet[key][colKey];
                  break;
                case 'roles':
                  const roleLabels = value
                    .toString()
                    .split(',')
                    .map((s) => s.trim().toLowerCase());
                  const parsedRoles = roleLabels
                    .map((label) => {
                      switch (label) {
                        case 'administrator':
                          return Role.Administrator;
                        case 'planner':
                          return Role.Planner;
                        case 'ritplanner':
                        case 'rideplanner':
                          return Role.RidePlanner;
                        case 'chauffeur':
                        case 'driver':
                          return Role.Driver;
                        case 'bijrijder':
                        case 'co-driver':
                        case 'co_driver':
                          return Role.CoDriver;
                        default:
                          return null;
                      }
                    })
                    .filter((r) => r !== null);
                  if (parsedRoles.length > 0) {
                    userData[colName] = parsedRoles;
                  }
                  break;
                case 'birthDate':
                case 'privacyPolicyAcceptDate':
                  const dateVal = spreadsheet[key][colKey];
                  userData[colName] =
                    valueToDate(dateVal) || this.transformDutchDateStringToDate(dateVal);
                  break;
                case 'hidden':
                  const valueStr = value?.toString().toUpperCase();
                  userData[colName] = valueStr === 'WAAR' || valueStr === 'TRUE';
                  break;
              }
              /* eslint-enable no-case-declarations */
            }
          }
        });

        // Convert emergencyPhoneNumbers object to array
        if (Object.keys(emergencyPhoneNumbers).length > 0) {
          userData.emergencyPhoneNumbers = Object.keys(emergencyPhoneNumbers)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map((index) => {
              const phone = emergencyPhoneNumbers[index];
              if (!phone.name) {
                phone.name = 'Onbekend';
              }
              if (!phone.description) {
                phone.description = 'Onbekend';
              }
              return phone;
            })
            .filter((phone) => phone.phoneNumber);
        }

        // Build full address string for validation
        let fullAddress: string | undefined;
        if (userData.streetName && userData.postal) {
          const parts = [userData.streetName, userData.houseNumber, userData.houseNumberAddition]
            .filter(Boolean)
            .join(' ');
          fullAddress = `${parts}, ${userData.postal}${userData.city ? ' ' + userData.city : ''}`;
        }

        usersToImport.push({
          userId,
          userData,
          fullAddress,
          originalRow: buildOriginalRow(spreadsheet[key]),
        });
      });

      const seenPassengerRows = new Set<string>();
      usersToImport = usersToImport.filter((user) => {
        const rowKey = JSON.stringify(user);
        if (seenPassengerRows.has(rowKey)) {
          return false;
        }
        seenPassengerRows.add(rowKey);
        return true;
      });

      const failedRows: (FailedImportRow & { originalRow: Record<string, any> })[] = [];
      const addFailedImportRow = (
        user: (typeof usersToImport)[number],
        error: string,
        address?: string,
      ) => {
        failedRows.push({
          name: user.userData.name || 'Onbekend',
          ...(address ? { address } : {}),
          error,
          user: user.userData.name,
          originalRow: user.originalRow,
        });
      };

      const existingPassengerSnapshots = new Map<string, any>();
      const referencedPassengerIds = [
        ...new Set(usersToImport.map((user) => user.userId).filter(Boolean) as string[]),
      ];
      try {
        await Promise.all(
          referencedPassengerIds.map(async (userId) => {
            existingPassengerSnapshots.set(
              userId,
              await getDoc(doc(db, `organisations/${this.organisationId}/users/${userId}`)),
            );
          }),
        );
      } catch (error) {
        console.error('Failed to load existing passengers:', error);
        this.snackBar.open('Er ging iets mis bij het ophalen van bestaande passagiers.', 'X', {
          duration: 5000,
        });
        return;
      }

      const getAddressFingerprint = (userData: UserId): string =>
        this.addressValidationService.normalizeAddressForComparison(
          `${userData.streetName ?? ''} ${userData.houseNumber ?? ''} ${userData.houseNumberAddition ?? ''}, ${userData.postal ?? ''} ${userData.city ?? ''}`,
        );

      const reuseExistingCoordinates = (
        user: (typeof usersToImport)[number],
        existingUserData: User,
      ): boolean => {
        const coordinates = existingUserData.addressCoordinates;
        const importedAddress = getAddressFingerprint(user.userData);
        const existingAddress = getAddressFingerprint(existingUserData as UserId);
        const storedAddress =
          existingUserData.addressCoordinatesFor ?? existingUserData.addressFingerprint;

        if (
          !coordinates ||
          !storedAddress ||
          importedAddress !== existingAddress ||
          importedAddress !== storedAddress
        ) {
          return false;
        }

        user.userData.addressCoordinates = { ...coordinates };
        user.userData.addressCoordinatesFor = importedAddress;
        user.userData.addressFingerprint = importedAddress;
        (user.userData as any).location = {
          _lat: coordinates.latitude,
          _long: coordinates.longitude,
        };
        validAddressStrings.add(user.fullAddress!);
        return true;
      };

      // Validate only new or changed addresses; unchanged existing addresses reuse ORS data.
      const validAddressStrings = new Set<string>();
      const validationResultsByAddress = new Map<string, any>();
      const usersToValidate: (typeof usersToImport)[number][] = [];

      // First, add users with incomplete addresses to failed list
      // (meaning they have some address fields filled in, but not enough to form a complete address)
      usersToImport.forEach((user) => {
        const hasAnyAddressField =
          user.userData.streetName ||
          user.userData.postal ||
          user.userData.houseNumber ||
          user.userData.houseNumberAddition ||
          user.userData.city;

        if (!user.fullAddress && hasAnyAddressField) {
          const error = 'Adres is incompleet of ontbreekt';
          addFailedImportRow(user, error);
        }
      });

      // Determine which complete addresses require a fresh ORS request.
      const usersWithAddresses = usersToImport.filter((user) => user.fullAddress);
      usersWithAddresses.forEach((user) => {
        const existingSnapshot = user.userId ? existingPassengerSnapshots.get(user.userId) : null;
        const hasReusableCoordinates =
          existingSnapshot?.exists() &&
          reuseExistingCoordinates(user, existingSnapshot.data() as User);

        if (!hasReusableCoordinates) {
          usersToValidate.push(user);
        }
      });

      if (usersToValidate.length > 0) {
        const addresses = [...new Set(usersToValidate.map((user) => user.fullAddress!))];
        const validationResult =
          await this.addressValidationService.validateAddressesBulk(addresses);

        // Build failed addresses list from validation results
        validationResult.invalidAddresses.forEach((invalidAddr) => {
          const error = invalidAddr.error
            ? translateAddressError(invalidAddr.error)
            : invalidAddr.requiresConfirmation
              ? 'Adres niet geïmporteerd: ORS kon het adres niet volledig verifiëren'
              : 'Adres niet gevonden via ORS';
          usersToValidate
            .filter((user) => user.fullAddress === invalidAddr.originalAddress)
            .forEach((user) => {
              addFailedImportRow(user, error, invalidAddr.originalAddress);
            });
        });

        // Track valid addresses
        validationResult.validAddresses.forEach((addr) => {
          validAddressStrings.add(addr.originalAddress);
          validationResultsByAddress.set(addr.originalAddress, addr);
        });
      }

      usersToValidate.forEach((user) => {
        const validation = validationResultsByAddress.get(user.fullAddress!);
        if (validation?.coordinates) {
          const addressFingerprint = getAddressFingerprint(user.userData);
          user.userData.addressCoordinates = { ...validation.coordinates };
          user.userData.addressCoordinatesFor = addressFingerprint;
          user.userData.addressFingerprint = addressFingerprint;
          (user.userData as any).location = {
            _lat: validation.coordinates.latitude,
            _long: validation.coordinates.longitude,
          };
        }
      });

      // Keep users with valid addresses OR users with no address fields at all
      const validUsers = usersToImport.filter((user) => {
        const hasAnyAddressField =
          user.userData.streetName ||
          user.userData.postal ||
          user.userData.houseNumber ||
          user.userData.houseNumberAddition ||
          user.userData.city;

        if (!hasAnyAddressField) {
          return true; // Completely empty address - skip validation and allow import
        }
        if (!user.fullAddress) {
          return false; // Incomplete address - exclude (already logged to failed list)
        }
        return validAddressStrings.has(user.fullAddress); // Complete address - must be valid
      });

      const getCallableFailureReason = (value: any): string => {
        const message = typeof value === 'string' ? value : (value?.data?.message ?? value?.data);
        switch (message) {
          case 'no-email-or-phone-provided':
            return 'Geen e-mailadres of telefoonnummer opgegeven';
          case 'auth/email-already-exists':
            return 'E-mailadres is al in gebruik';
          case 'auth/phone-number-already-exists':
            return 'Telefoonnummer is al in gebruik';
          case 'email_already_exists':
            return 'Het e-mailadres hoort niet bij het opgegeven passagier-ID';
          default:
            return 'Passagier kon niet worden aangemaakt';
        }
      };

      // Build promises for valid users
      for (const user of validUsers) {
        const userDataWithoutRoles = { ...user.userData } as any;
        delete userDataWithoutRoles.roles;

        if (user.userId) {
          const promise = (async () => {
            const orgUserSnap = existingPassengerSnapshots.get(user.userId);
            let signInMethod: string | undefined;

            if (orgUserSnap.exists()) {
              const orgUserData = orgUserSnap.data();
              signInMethod = orgUserData?.signInMethod ?? 'email';

              // 1. Email check
              if (userDataWithoutRoles.email && orgUserData) {
                const newEmail = String(userDataWithoutRoles.email).trim().toLowerCase();
                const currentEmail = orgUserData.email
                  ? String(orgUserData.email).trim().toLowerCase()
                  : '';

                if (newEmail !== currentEmail) {
                  if (signInMethod === 'email') {
                    return {
                      kind: 'failed',
                      reason: 'Het e-mailadres hoort niet bij het opgegeven passagier-ID',
                      user,
                    };
                  } else {
                    userDataWithoutRoles.email = newEmail;
                  }
                } else {
                  userDataWithoutRoles.email = newEmail;
                }
              }

              // 2. Phone number check
              if (userDataWithoutRoles.phoneNumber && orgUserData) {
                const newPhone = this.cleanPhone(userDataWithoutRoles.phoneNumber);
                const currentPhone = orgUserData.phoneNumber
                  ? this.cleanPhone(orgUserData.phoneNumber)
                  : '';

                if (newPhone !== currentPhone) {
                  if (signInMethod === 'phoneNumber') {
                    return {
                      kind: 'failed',
                      reason: 'Het telefoonnummer hoort niet bij het opgegeven passagier-ID',
                      user,
                    };
                  } else {
                    // Check if phone is already in use by another user in the organisation
                    const phoneQuery = query(
                      collection(db, `organisations/${this.organisationId}/users`),
                      where('phoneNumber', '==', newPhone),
                      limit(1),
                    );
                    const phoneSnap = await getDocs(phoneQuery);
                    const phoneUsed =
                      !phoneSnap.empty && phoneSnap.docs.some((d) => d.id !== user.userId);
                    if (phoneUsed) {
                      delete userDataWithoutRoles.phoneNumber;
                    } else {
                      userDataWithoutRoles.phoneNumber = newPhone;
                    }
                  }
                } else {
                  userDataWithoutRoles.phoneNumber = newPhone;
                }
              }
            }

            const callable = httpsCallable(functions, 'httpAddUser');
            const result: any = await callable({
              userId: user.userId,
              email: userDataWithoutRoles.email,
              phoneNumber: userDataWithoutRoles.phoneNumber,
              organisationId: this.organisationId,
              passenger: true,
              saveObj: userDataWithoutRoles,
            });

            // Grant rights if needed, using the final resolved email and phone
            const finalEmail =
              userDataWithoutRoles.email !== undefined
                ? userDataWithoutRoles.email
                : orgUserSnap.exists()
                  ? orgUserSnap.data()?.email
                  : '';

            const finalPhone =
              userDataWithoutRoles.phoneNumber !== undefined
                ? userDataWithoutRoles.phoneNumber
                : orgUserSnap.exists()
                  ? orgUserSnap.data()?.phoneNumber
                  : '';

            if (result?.data?.message !== 'succeed') {
              return { kind: 'failed', reason: getCallableFailureReason(result), user };
            }
            if (user.userData.roles && user.userData.roles.length > 0) {
              try {
                const rightsResult = await this.grantRights({
                  ...user.userData,
                  email: finalEmail,
                  phoneNumber: finalPhone,
                  isNewUser: false,
                  id: result.data.userId,
                });
                if (rightsResult !== 'success') {
                  return {
                    kind: 'partial',
                    reason: 'Rechten konden niet worden toegevoegd',
                    user,
                  };
                }
              } catch {
                return {
                  kind: 'partial',
                  reason: 'Rechten konden niet worden toegevoegd',
                  user,
                };
              }
            }
            return { kind: 'success', user };
          })().catch((error) => ({
            kind: 'failed',
            reason: getCallableFailureReason(error),
            user,
          }));
          promises.push(promise);
        } else {
          userDataWithoutRoles.hidden ??= false;
          if (user.userData.email) {
            const cleanedEmail = String(user.userData.email).trim().toLowerCase();
            user.userData.email = cleanedEmail;
            userDataWithoutRoles.email = cleanedEmail;
          }
          if (user.userData.phoneNumber) {
            const cleanedPhone = this.cleanPhone(user.userData.phoneNumber);
            user.userData.phoneNumber = cleanedPhone;
            userDataWithoutRoles.phoneNumber = cleanedPhone;
          }

          const callable = httpsCallable(functions, 'httpAddUser');
          const params = {
            email: user.userData.email,
            phoneNumber: user.userData.phoneNumber,
            organisationId: this.organisationId,
            passenger: true,
            saveObj: userDataWithoutRoles,
          };
          const promise: Promise<any> = callable(params)
            .then(async (result: any) => {
              if (result?.data?.message !== 'succeed') {
                return { kind: 'failed', reason: getCallableFailureReason(result), user };
              }
              if (user.userData.roles && user.userData.roles.length > 0) {
                try {
                  const rightsResult = await this.grantRights({
                    ...user.userData,
                    passenger: true,
                    isNewUser: false,
                    id: result?.data?.userId,
                  });
                  if (rightsResult !== 'success') {
                    return {
                      kind: 'partial',
                      reason: 'Rechten konden niet worden toegevoegd',
                      user,
                    };
                  }
                } catch {
                  return {
                    kind: 'partial',
                    reason: 'Rechten konden niet worden toegevoegd',
                    user,
                  };
                }
              }
              return { kind: 'success', user };
            })
            .catch((error) => ({
              kind: 'failed',
              reason: getCallableFailureReason(error),
              user,
            }));
          promises.push(promise);
        }
      }

      console.log('Importing users, total:', promises.length);
      let completedImports = 0;
      const results = await Promise.allSettled(
        promises.map((promise) =>
          promise.finally(() => {
            completedImports++;
            this.importProgress.set(
              promises.length ? Math.round((completedImports / promises.length) * 100) : 100,
            );
          }),
        ),
      );

      let successCount = 0;
      let partialCount = 0;
      let failureCount = failedRows.length;

      for (const result of results) {
        if (result.status === 'fulfilled') {
          const val = result.value as any;
          if (val.kind === 'success') {
            successCount++;
          } else if (val.kind === 'partial') {
            partialCount++;
            addFailedImportRow(val.user, val.reason);
          } else {
            failureCount++;
            addFailedImportRow(val.user, val.reason);
          }
        } else {
          failureCount++;
          console.error('Import error:', result.reason);
        }
      }

      let failedRowsDownloadSucceeded = true;
      if (failedRows.length > 0) {
        try {
          await exportJsonToExcel(
            failedRows.map(({ originalRow, error }) => ({
              ...originalRow,
              'Reden van fout bij importeren': error,
            })),
            'Passagiers-import-fouten',
            'Passagiers',
          );
        } catch (error) {
          failedRowsDownloadSucceeded = false;
          console.error('Failed to export passenger import errors:', error);
        }
      }

      if (partialCount > 0 || (successCount > 0 && failureCount > 0)) {
        this.snackBar.open(
          `Het importeren van de passagiers is gedeeltelijk voltooid: ${successCount} gelukt, ${partialCount} gedeeltelijk gelukt, ${failureCount} mislukt.`,
          'X',
          {
            duration: 30000,
          },
        );
      } else if (failureCount > 0) {
        this.snackBar.open(
          `Het importeren van de passagiers is mislukt: ${failureCount} mislukt.`,
          'X',
          { duration: 30000 },
        );
      } else {
        this.snackBar.open('Het importeren van de passagiers is voltooid.', 'X', {
          duration: 3000,
        });
      }
      if (failedRows.length > 0) {
        this.showFailedImportRows(
          failedRows,
          successCount > 0 || partialCount > 0,
          failedRowsDownloadSucceeded,
        );
      }
    } catch (error) {
      console.error('Passenger import error:', error);
      this.snackBar.open('Er is iets misgegaan tijdens het importeren van de passagiers.', 'X', {
        duration: 5000,
      });
    } finally {
      this.processing.set(false);
      this.importing.set(false);
      this.importProgress.set(0);
      if (this.uploader?.nativeElement) {
        this.uploader.nativeElement.value = '';
      }
    }
  }

  private async grantRights(userData: any): Promise<any> {
    const isAuthorized = this.isSuperUser() || this.roles?.includes(Role.Administrator);
    if (!isAuthorized) {
      return;
    }
    const callable = httpsCallable(functions, 'httpGrantRights');
    const params = {
      email: userData.email,
      password: null,
      name: ucFirst(userData.name || ''),
      roles: userData.roles || [],
      organisationId: this.organisationId,
      phone: userData.phone || userData.phoneNumber || '',
      hidden: userData.hidden ?? false,
      withdrawRights: false,
      passenger: userData.passenger ?? false,
      userId: userData.id ?? null,
    };

    const result = await callable(params);
    console.log('grantRights', result);
    return result.data;
  }

  private showFailedImportRows(
    failedRows: FailedImportRow[],
    hasSuccessfulImports: boolean = false,
    downloadSucceeded: boolean = true,
  ) {
    this.dialog.open(WarningDialog, {
      width: '700px',
      data: {
        dialogType: 'importFailedRows',
        optionalData: {
          failedRows,
          hasSuccessfulImports,
          downloadSucceeded,
        },
      },
    });
  }

  private cleanPhone(phone: any): string {
    if (!phone) return '';
    let phoneNumber = phone.toString().replace(/[^\d+]/g, '');
    if (/^6\d{8}$/.test(phoneNumber)) {
      phoneNumber = `0${phoneNumber}`;
    }
    if (phoneNumber.startsWith('06')) {
      phoneNumber = `+316${phoneNumber.substring(2, phoneNumber.length)}`;
    }
    return phoneNumber;
  }

  transformDutchDateStringToDate(dutchDateString: any) {
    if (!dutchDateString || typeof dutchDateString !== 'string') {
      return null;
    }
    const splitDateTime = dutchDateString.split(' ');
    const dateArray = splitDateTime[0].split('-');
    if (dateArray.length !== 3) {
      return null;
    }
    let timeString = '';
    if (splitDateTime.length > 1) {
      // Includes time aswell
      timeString = ` ${splitDateTime[1]}`;
    }
    const dateString = `${dateArray[2]}-${dateArray[1]}-${dateArray[0]}${timeString}`;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }

  async export(exportType: UserExportSelection) {
    if (this.processing()) {
      return;
    }
    this.processing.set(true);

    const usersExcel = [];
    let usersToExport: any[];
    switch (exportType) {
      case 'filtered':
        usersToExport = this.filteredUsers();
        break;
      case 'current':
        usersToExport = this.allUsersList.filter(
          (user) => !user.deleted && Boolean(user.hidden) === this.filterForm.value.showHidden,
        );
        break;
      case 'all':
      default:
        usersToExport = this.allUsersList;
        break;
    }
    usersToExport = usersToExport.filter((user) => !user.superuser);

    const sortedUsers = [...usersToExport].sort((a, b) => {
      const nameA = a.name ? a.name.toLowerCase() : '';
      const nameB = b.name ? b.name.toLowerCase() : '';
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    let maxEmergencyContacts = 0;
    sortedUsers.forEach((element) => {
      if (
        element.emergencyPhoneNumbers &&
        element.emergencyPhoneNumbers.length > maxEmergencyContacts
      ) {
        maxEmergencyContacts = element.emergencyPhoneNumbers.length;
      }
    });
    const emergencyCount = Math.max(1, maxEmergencyContacts);

    sortedUsers.forEach((element: OrgUser) => {
      const birthDate = element.birthDate ? valueToDate(element.birthDate) : '';
      const createdAt = element.createdAt ? valueToDate(element.createdAt) : '';
      const privacyPolicyAcceptence = element.privacyPolicyAcceptDate
        ? valueToDate(element.privacyPolicyAcceptDate)
        : '';
      let roles = element.roles;
      if (!Array.isArray(roles)) {
        roles = roles ? [roles] : [];
      }

      let RoleStr = roles
        .map((role) => {
          switch (role) {
            case Role.Administrator:
              return 'Administrator';
            case Role.Driver:
              return 'Chauffeur';
            case Role.CoDriver:
              return 'Bijrijder';
            case Role.Planner:
              return 'Planner';
            case Role.RidePlanner:
              return 'Ritplanner';
            default:
              return role;
          }
        })
        .join(', ');

      if (element.passenger !== false) {
        RoleStr = RoleStr ? 'Passagier, ' + RoleStr : 'Passagier';
      }

      const objectToPush = {
        Kaartnummer: element.cardId ? element.cardId : '',
        Rol: RoleStr,
        'Gemaakt op': createdAt,
        Naam: element.name ? element.name : '',
        'E-mail': element.email ? element.email : '',
        Telefoonnummer: element.phoneNumber ? element.phoneNumber : '',
        Straatnaam: element.streetName ? element.streetName : '',
        Huisnummer: element.houseNumber ? element.houseNumber : '',
        'Huisnummer toevoeging': element.houseNumberAddition ? element.houseNumberAddition : '',
        Postcode: element.postal ? element.postal : '',
        Stad: element.city ? element.city : '',
        Geboortedatum: birthDate,
        Geslacht: element.gender ? element.gender : '',
        'Plan naam': element.currentPlan ? element.currentPlan.name : '',
        Credits: element.credits ? element.credits : '',
        'Acceptatiedatum privacybeleid': privacyPolicyAcceptence,
        'Opmerking (Algemeen)': element.hiddenRemarks ? element.hiddenRemarks : '',
        'Opmerking voor planner': element.remarks ? element.remarks : '',
        'Opmerking voor chauffeur': element.commentsForDriver ? element.commentsForDriver : '',
      };
      this.particularities.forEach((particularity) => {
        objectToPush[particularity.name] = element.particularityIds?.includes(particularity.id)
          ? 'WAAR'
          : 'ONWAAR';
      });
      objectToPush['Passagier ID'] = element.id;
      // Add emergency phone numbers as separate columns
      for (let i = 0; i < emergencyCount; i++) {
        const prefix = i === 0 ? 'Noodnummer' : `Noodnummer ${i}`;
        if (element.emergencyPhoneNumbers && element.emergencyPhoneNumbers[i]) {
          objectToPush[`${prefix} naam`] = element.emergencyPhoneNumbers[i].name || '';
          objectToPush[`${prefix} telefoon`] = element.emergencyPhoneNumbers[i].phoneNumber || '';
          objectToPush[`${prefix} relatie`] = element.emergencyPhoneNumbers[i].description || '';
        } else {
          objectToPush[`${prefix} naam`] = '';
          objectToPush[`${prefix} telefoon`] = '';
          objectToPush[`${prefix} relatie`] = '';
        }
      }
      if (exportType !== 'filtered') {
        objectToPush['Verborgen'] = element.hidden ? 'WAAR' : 'ONWAAR';
      }
      usersExcel.push(objectToPush);
    });
    await exportJsonToExcel(usersExcel, 'Passagiers', 'Passagiers');
    await logExport('users');
    this.processing.set(false);
  }
}
